import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  PhotoLibrary as PhotoIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { auditLogService } from '../../services/auditLogService';
import { AdminStats, AuditLog } from '../../types/admin';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

/**
 * AdminAnalytics Component
 * Analytics dashboard showing usage statistics and trends
 *
 * Features:
 * - User growth metrics
 * - Content statistics
 * - Activity trends
 * - Date range filtering
 * - Export to CSV
 * - Visual charts and graphs
 */
const AdminAnalytics: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(30); // Days

  /**
   * Load analytics data on mount
   */
  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  /**
   * Load analytics data
   */
  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load admin stats
      const loadedStats = await adminService.getAdminStats();
      setStats(loadedStats);

      // Load recent activity
      const startDate = startOfDay(subDays(new Date(), dateRange));
      const endDate = endOfDay(new Date());
      const result = await auditLogService.getAuditLogs(
        { startDate, endDate },
        1000 // Get up to 1000 logs for analytics
      );
      setRecentActivity(result.logs);
    } catch (err: any) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Export analytics to CSV
   */
  const handleExport = () => {
    if (!stats) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Total Users', stats.users.total],
      ['Active Users', stats.users.activeThisWeek],
      ['Pending Approvals', stats.users.pending],
      ['Suspended Users', stats.users.suspended],
      ['Total Persons', stats.content.totalPersons],
      ['Total Photos', stats.content.totalPhotos],
      ['Total Relationships', stats.content.totalRelationships],
      ['Flagged Content', stats.moderation.pendingFlags],
      ['', ''],
      ['Activity (Last ' + dateRange + ' Days)', ''],
      ...recentActivity.map((entry) => [
        format(entry.timestamp, 'yyyy-MM-dd HH:mm:ss'),
        `${entry.action} by ${entry.userEmail}`,
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Calculate activity by type
   */
  const getActivityByType = () => {
    const activityCounts: Record<string, number> = {};
    recentActivity.forEach((entry) => {
      activityCounts[entry.action] = (activityCounts[entry.action] || 0) + 1;
    });
    return activityCounts;
  };

  /**
   * Calculate daily activity trend
   */
  const getDailyActivityTrend = () => {
    const dailyCounts: Record<string, number> = {};
    recentActivity.forEach((entry) => {
      const dateKey = format(entry.timestamp, 'yyyy-MM-dd');
      dailyCounts[dateKey] = (dailyCounts[dateKey] || 0) + 1;
    });
    return dailyCounts;
  };

  /**
   * Render loading state
   */
  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Failed to load analytics data</Alert>
      </Container>
    );
  }

  const activityByType = getActivityByType();
  const dailyActivity = getDailyActivityTrend();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                Analytics Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Usage statistics and activity trends
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(Number(e.target.value))}
                  label="Date Range"
                >
                  <MenuItem value={7}>Last 7 Days</MenuItem>
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                  <MenuItem value={90}>Last 90 Days</MenuItem>
                  <MenuItem value={365}>Last Year</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAnalytics}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={loading}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Overview Statistics */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.users.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Active: {stats.users.activeThisWeek}
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    Pending: {stats.users.pending}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.content.totalPersons}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Family Members
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Profiles in database
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <PhotoIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.content.totalPhotos}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Photos
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Uploaded by users
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TimelineIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.content.totalRelationships}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Relationships
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Family connections
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Activity Statistics */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Activity by Type (Last {dateRange} Days)
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {Object.keys(activityByType).length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  No activity recorded in this period
                </Typography>
              ) : (
                <Box>
                  {Object.entries(activityByType)
                    .sort(([, a], [, b]) => b - a)
                    .map(([action, count]) => (
                      <Box key={action} mb={2}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {action.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {count} actions
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(count / recentActivity.length) * 100}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <TimelineIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Daily Activity Trend
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {Object.keys(dailyActivity).length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  No activity recorded in this period
                </Typography>
              ) : (
                <Box>
                  {Object.entries(dailyActivity)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .slice(-14) // Show last 14 days
                    .map(([date, count]) => (
                      <Box key={date} mb={2}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {format(new Date(date), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {count} actions
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(count / Math.max(...Object.values(dailyActivity))) * 100}%`,
                              height: '100%',
                              bgcolor: 'success.main',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* User Distribution */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                User Status Distribution
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={2}>
                    <Typography variant="h3" fontWeight={700} color="success.dark">
                      {stats.users.activeThisWeek}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      Active Users
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {((stats.users.activeThisWeek / stats.users.total) * 100).toFixed(1)}% of total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={2}>
                    <Typography variant="h3" fontWeight={700} color="warning.dark">
                      {stats.users.pending}
                    </Typography>
                    <Typography variant="body2" color="warning.dark">
                      Pending Approval
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {((stats.users.pending / stats.users.total) * 100).toFixed(1)}% of total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="error.light" borderRadius={2}>
                    <Typography variant="h3" fontWeight={700} color="error.dark">
                      {stats.users.suspended}
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      Suspended
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {((stats.users.suspended / stats.users.total) * 100).toFixed(1)}% of total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={2}>
                    <Typography variant="h3" fontWeight={700} color="info.dark">
                      {stats.users.total - stats.users.activeThisWeek - stats.users.pending - stats.users.suspended}
                    </Typography>
                    <Typography variant="body2" color="info.dark">
                      Inactive
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(((stats.users.total - stats.users.activeThisWeek - stats.users.pending - stats.users.suspended) / stats.users.total) * 100).toFixed(1)}% of total
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Content Moderation
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box p={2} bgcolor="warning.light" borderRadius={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" fontWeight={600} color="warning.dark">
                        Flagged Content
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="warning.dark">
                        {stats.moderation.pendingFlags}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" mt={1} display="block">
                      Pending review
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box p={2} bgcolor="grey.100" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">
                      Total Activity (Last {dateRange} Days)
                    </Typography>
                    <Typography variant="h4" fontWeight={700} mt={1}>
                      {recentActivity.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Admin actions recorded
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Activity Summary */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Recent Activity Summary
          </Typography>
          <Divider sx={{ mb: 3 }} />
          {recentActivity.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No recent activity
            </Typography>
          ) : (
            <Box>
              {recentActivity.slice(0, 10).map((entry, index) => (
                <Box
                  key={entry.id}
                  py={2}
                  borderBottom={index < 9 ? '1px solid' : 'none'}
                  borderColor="divider"
                >
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.action.replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        by {entry.userEmail}
                      </Typography>
                      {entry.details && (
                        <Typography variant="caption" color="text.secondary">
                          {JSON.stringify(entry.details).substring(0, 100)}...
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {format(entry.timestamp, 'MMM dd, HH:mm')}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminAnalytics;
